import type { UserState } from "@/redux/features/activeUserSlice"
import { useEffect, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import Headroom from "react-headroom"
import DropdownNavItem from "@/components/DropdownNavItem"
import Hamburger from "@/components/Hamburger"
import Logo from "@/components/Logo"
import MobileNav from "@/components/MobileNav"
import NavItem from "@/components/NavItem"
import SingleInputSignupForm from "@/components/SingleInputSignupForm"
import UserNavItem from "@/components/UserNavItem"
import { BREAKPOINTS_MIN } from "@/libs/constants"
import useMediaQuery from "@/libs/custom-hooks/useMediaQuery"
import * as styles from "@/styles/modules/Header.module.scss"

interface HeaderProps {
  activeUser: UserState
  forceBackground: boolean
  forceTheme?: "light" | "dark"
  showSignupBanner?: boolean
}

export default function Header({
  activeUser,
  forceBackground = false,
  forceTheme,
  showSignupBanner = true,
}: HeaderProps) {
  const isTablet = useMediaQuery(BREAKPOINTS_MIN.tabletLarge)
  const data = useStaticQuery(graphql`
    {
      allSanityNavigation(filter: { navId: { eq: "primaryNav" } }) {
        nodes {
          items {
            _key
            text
            navItemUrl {
              internalLink
              blank
              href
              linkType
              title
            }
            loggedInOnly
            loggedOutOnly
            hasSubnav
            subLinks {
              _key
              emoji
              text
              title
              slug {
                current
              }
            }
          }
          navId
        }
      }
    }
  `)

  // State used by headroom to enable / disable dropdowns
  // based on pinned or unpinned
  const [enableDropdowns, setEnableDropdowns] = useState(true)
  const [mobileNavIsOpen, setMobileNavIsOpen] = useState(false)

  useEffect(() => {
    isTablet && setMobileNavIsOpen(false)
  }, [isTablet])

  const navItems = data.allSanityNavigation.nodes[0].items

  function handleHamburgerClick() {
    setMobileNavIsOpen(state => !state)
  }

  return (
    <>
      <Headroom
        className={[
          styles.wrapper,
          forceBackground ? styles.forceBackground : "",
          mobileNavIsOpen ? styles.mobileNavIsOpen : "",
          forceTheme ? styles[`force${forceTheme}`] : "",
        ].join(" ")}
        disableInlineStyles
        upTolerance={5}
        downTolerance={10}
        onUnpin={() => setEnableDropdowns(false)}
        onPin={() => setEnableDropdowns(true)}
      >
        <header className={styles.container}>
          <Logo
            flavor={
              forceTheme && !mobileNavIsOpen
                ? forceTheme === "light"
                  ? "chocolate"
                  : "vanilla"
                : undefined
            }
            className={styles.logo}
            linkTo="/"
            title="Visit the DONUT Homepage"
          />

          {isTablet ? (
            <>
              <nav className={styles.nav}>
                {navItems.map(item => {
                  return (
                    ((item.loggedInOnly && activeUser.ambassadorPermission) ||
                      (!item.loggedInOnly && !item.loggedOutOnly) ||
                      (item.loggedOutOnly &&
                        !activeUser.ambassadorPermission)) && (
                      <NavItem
                        key={item._key}
                        itemText={item.text}
                        linkData={item.navItemUrl}
                        enableDropdowns={enableDropdowns}
                        forceTheme={forceTheme}
                      >
                        {item.hasSubnav &&
                          item.subLinks.map(link => {
                            return (
                              <DropdownNavItem
                                key={link._key}
                                itemText={link.text}
                                emoji={link.emoji}
                                title={link.title}
                                parentLinkData={item.navItemUrl}
                                url={`${
                                  item.navItemUrl.internalLink
                                    ? item.navItemUrl.internalLink
                                    : ""
                                }/${link.slug.current}`}
                              />
                            )
                          })}
                      </NavItem>
                    )
                  )
                })}
              </nav>
              {activeUser.ambassadorPermission ? (
                <UserNavItem
                  identifier={activeUser.email || activeUser.phone}
                  forceTheme={forceTheme}
                />
              ) : null}
            </>
          ) : (
            <Hamburger
              className={styles.menuIcon}
              isOpen={mobileNavIsOpen}
              onClick={handleHamburgerClick}
            />
          )}
        </header>
        {isTablet && !activeUser.ambassadorPermission && showSignupBanner && (
          <div className={styles.headerFormWrapper}>
            <p className="small">
              Fast, unbiased news that&apos;s actually enjoyable to read 👉
            </p>
            <SingleInputSignupForm
              wrapperClass={styles.headerForm}
              submitBtnText="Subscribe"
              size="small"
              formName="Header Subscribe Form"
            />
          </div>
        )}
      </Headroom>
      {!isTablet && (
        <MobileNav
          navItems={navItems}
          activeUser={activeUser}
          isOpen={mobileNavIsOpen}
        />
      )}
    </>
  )
}
